import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery, useQueryClient } from 'react-query';
import clsx from 'clsx';
import { useLanguage } from '../../contexts/LanguageContext';
import PageHeader from '../../components/ui/PageHeader';
import UiCard from '../../components/ui/UiCard';
import FormPageLayout from '../../components/ui/FormPageLayout';
import FilterSelect from '../../components/ui/FilterSelect';
import { paginationItems, FORMA_LINK_BTN as FORMA_LINK_BUTTON_CLASS } from '../../utils/listPageUi';
import { formationAPI, spaEntityAPI } from '../../services/api';
import { fetchFormationEnrollmentBundle, enrichBatchWithEnrollment, countActiveEnrollmentsByBatch, countEnrolledStudentsForCourse, resolveRoomLabel, batchBelongsToCourse, getEnrolledStudentsForCourse } from '../../utils/formationEnrollmentBundle';
import { invalidateFormationGraph } from '../../utils/invalidateFormationGraph';
import { SPA_COURSES_KEY, SPA_CLASSROOMS_KEY, FORMATION_ENROLLMENT_BUNDLE_KEY, FORMATION_STUDENTS_CATALOG_KEY } from '../../utils/queryKeys';
import { checkTrainerBatchConflict } from '../../utils/trainerBatchConflict';
import { confirmDialog } from '../../utils/confirmBridge';
import notify from '../../utils/notify';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ClipboardDocumentCheckIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  AcademicCapIcon,
  CurrencyDollarIcon,
  ClockIcon,
  ArrowLeftIcon,
  BuildingOfficeIcon,
  TagIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

const BatchForm = ({ courseId, courseName, onBatchCreated, onCancel, isEditing = false, batchData = null, allBatchesForConflict = [] }) => {
  const { t } = useLanguage();
  const [trainers, setTrainers] = useState([]);
  const { data: classrooms = [] } = useQuery(
    SPA_CLASSROOMS_KEY,
    () => spaEntityAPI.list('classrooms'),
    { staleTime: 15_000, refetchOnWindowFocus: true }
  );
  const [formData, setFormData] = useState({
    batchName: batchData?.batchName || '',
    days: batchData?.days || [],
    startTime: batchData?.startTime || '',
    endTime: batchData?.endTime || '',
    startDate: batchData?.startDate || '',
    endDate: batchData?.endDate || '',
    trainerId: batchData?.trainerId || '',
    room: batchData?.room || '',
    maxCapacity: batchData?.maxCapacity || 15
  });
  const [conflicts, setConflicts] = useState([]);

  // Load trainers from D1
  useEffect(() => {
    let cancelled = false;
    spaEntityAPI.list('trainers').then((list) => {
      if (!cancelled) setTrainers(list || []);
    });
    return () => { cancelled = true; };
  }, []);

  const weekDays = [
    { id: 'Monday', name: 'Lundi' },
    { id: 'Tuesday', name: 'Mardi' },
    { id: 'Wednesday', name: 'Mercredi' },
    { id: 'Thursday', name: 'Jeudi' },
    { id: 'Friday', name: 'Vendredi' },
    { id: 'Saturday', name: 'Samedi' },
    { id: 'Sunday', name: 'Dimanche' }
  ];

  const handleDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      days: prev.days.includes(day) 
        ? prev.days.filter(d => d !== day)
        : [...prev.days, day]
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Check for conflicts when trainer, days, times, or dates change
  useEffect(() => {
    if (formData.trainerId && formData.days.length > 0 && formData.startTime && formData.endTime && formData.startDate && formData.endDate) {
      const conflictingBatches = checkTrainerBatchConflict(
        allBatchesForConflict,
        formData.trainerId,
        formData.days,
        formData.startTime,
        formData.endTime,
        formData.startDate,
        formData.endDate,
        isEditing ? batchData?.id : null
      );
      setConflicts(conflictingBatches);
    } else {
      setConflicts([]);
    }
  }, [formData.trainerId, formData.days, formData.startTime, formData.endTime, formData.startDate, formData.endDate, isEditing, batchData?.id, allBatchesForConflict]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (conflicts.length > 0) {
      notify.show(t('coursesPage.trainerConflictAlert'));
      return;
    }

    try {
      const selectedTrainer = trainers.find(t => t.id === formData.trainerId);
      
      const batchDataToSave = {
        courseId,
        courseName,
        ...formData,
        startDate: formData.startDate || null,
        endDate: formData.endDate || null,
        trainerName: selectedTrainer?.name || 'Unknown',
      };

      if (isEditing && batchData) {
        const updatedBatch = { ...batchData, ...batchDataToSave, id: batchData.id };
        const saved = await formationAPI.updateBatch(batchData.id, updatedBatch);
        onBatchCreated(saved);
      } else {
        const id = `batch-${Date.now()}`;
        const saved = await formationAPI.createBatch({ ...batchDataToSave, id, status: 'active' });
        onBatchCreated(saved);
        setFormData({
          batchName: '',
          days: [],
          startTime: '',
          endTime: '',
          startDate: '',
          endDate: '',
          trainerId: '',
          room: '',
          maxCapacity: 15,
        });
      }
    } catch (error) {
      console.error('Error saving batch:', error);
      notify.show(t('coursesPage.batchSaveError'));
    }
  };

  return (
    <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-6">
      <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4">
        {isEditing ? t('coursesPage.batchFormTitleEdit') : t('coursesPage.batchFormTitleNew')}
      </h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Nom du groupe
            </label>
            <input
              type="text"
              name="batchName"
              value={formData.batchName}
              onChange={handleChange}
              placeholder={`${courseName} - Groupe A`}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              required
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Jours de la semaine
            </label>
            <div className="flex flex-wrap gap-2">
              {weekDays.map(day => (
                <button
                  key={day.id}
                  type="button"
                  onClick={() => handleDayToggle(day.id)}
                  className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                    formData.days.includes(day.id)
                      ? 'bg-forma-600 text-white'
                      : 'bg-slate-50 dark:bg-slate-800/80 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                >
                  {day.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Heure de début
            </label>
            <input
              type="time"
              name="startTime"
              value={formData.startTime}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              required
            />
          </div>

          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Heure de fin
            </label>
            <input
              type="time"
              name="endTime"
              value={formData.endTime}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              required
            />
          </div>

          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('coursesPage.batchLblStartDateShort')} ({t('coursesPage.optionalSuffix')})
            </label>
            <input
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
            />
          </div>

          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('coursesPage.batchLblEndDateShort')} ({t('coursesPage.optionalSuffix')})
            </label>
            <input
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
            />
          </div>

          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Formateur
            </label>
            <select
              name="trainerId"
              value={formData.trainerId}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              required
            >
              <option value="">{t('coursesPage.batchSelectTrainer')}</option>
              {trainers.map(trainer => (
                <option key={trainer.id} value={trainer.id}>
                  {trainer?.name || `${t('coursesPage.trainerFallbackNamed', { id: trainer.id })}`}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('coursesPage.batchLblMaxCapacity')}
            </label>
            <input
              type="number"
              name="maxCapacity"
              value={formData.maxCapacity}
              onChange={handleChange}
              min="1"
              max="50"
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              required
            />
          </div>

          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('coursesPage.batchLblRoomOptional')}
            </label>
            <select
              name="room"
              value={formData.room}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
            >
              <option value="">{t('coursesPage.batchRoomPlaceholder')}</option>
              {classrooms.map((classroom) => (
                <option key={classroom.id} value={classroom.id}>
                  {t('coursesPage.roomPickMetaLine', {
                    name: classroom.name,
                    code: classroom.code,
                    capacity: classroom.capacity,
                    occupied: !classroom.isAvailable ? t('coursesPage.batchRoomOccupiedSuffix').trim() : '',
                  })}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Enhanced Conflict warnings */}
        {conflicts.length > 0 && (
          <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4">
            <div className="flex items-center mb-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-400 mr-2" />
              <div>
                <span className="text-red-400 font-medium text-lg">⚠️ Conflits d'horaires détectés!</span>
                <p className="text-red-300 text-xs mt-1">Le formateur sélectionné a déjà des cours aux mêmes créneaux</p>
              </div>
            </div>
            
            <div className="bg-red-500/10 rounded-lg p-3 mb-3">
              <h4 className="text-red-300 font-medium text-sm mb-2">{t('coursesPage.conflictingCoursesHdr')}</h4>
              <div className="space-y-2">
                {conflicts.map(conflict => (
                  <div key={conflict.id} className="bg-red-500/20 rounded-lg p-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-red-200 font-medium">{conflict.courseName}</div>
                        <div className="text-red-300 text-xs">{conflict.batchName}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-red-200 text-sm">{conflict.days.join(', ')}</div>
                        <div className="text-red-300 text-xs">{conflict.startTime} - {conflict.endTime}</div>
                      </div>
                    </div>
                    {conflict.room && (
                      <div className="text-red-300 text-xs mt-1">{t('coursesPage.conflictRoomLbl')} {conflict.room}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
              <h4 className="text-yellow-300 font-medium text-sm mb-2">💡 Solutions suggérées:</h4>
              <ul className="text-yellow-200 text-xs space-y-1">
                <li>• Choisir un autre formateur disponible</li>
                <li>• Modifier les jours de la semaine</li>
                <li>• Changer les heures de début/fin</li>
                <li>• Reporter ce groupe à une autre période</li>
              </ul>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-slate-50 dark:bg-slate-800/80 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors rounded-lg text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50"
          >
            {t('trainerForm.cancel')}
          </button>
          <button
            type="submit"
            disabled={conflicts.length > 0}
            className={clsx(
              FORMA_LINK_BUTTON_CLASS,
              'disabled:cursor-not-allowed disabled:opacity-50'
            )}
          >
            {isEditing ? t('coursesPage.batchSubmitEdit') : t('coursesPage.batchSubmitCreate')}
          </button>
        </div>
      </form>
    </div>
  );
};

const CourseDetail = ({ course }) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [showBatchForm, setShowBatchForm] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [selectedBatchStudents, setSelectedBatchStudents] = useState(null);
  const [showStudentsModal, setShowStudentsModal] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);

  const { data: classrooms = [] } = useQuery(
    SPA_CLASSROOMS_KEY,
    () => spaEntityAPI.list('classrooms'),
    { staleTime: 15_000, refetchOnWindowFocus: true }
  );

  const { data: bundleData, refetch: refetchBatches } = useQuery(
    FORMATION_ENROLLMENT_BUNDLE_KEY,
    fetchFormationEnrollmentBundle,
    { staleTime: 5000, refetchOnWindowFocus: true }
  );

  const batches = useMemo(
    () => (bundleData?.batches || []).filter((b) => batchBelongsToCourse(b, course)),
    [bundleData?.batches, course]
  );

  const allBatchesForConflict = bundleData?.batches || [];
  const enrollmentsCatalog = bundleData?.enrollments || [];

  const { data: studentsCatalog = [] } = useQuery(
    FORMATION_STUDENTS_CATALOG_KEY,
    async () => (await formationAPI.getStudents({ limit: 500, page: 1 })).items || [],
    { staleTime: 15_000, refetchOnWindowFocus: true }
  );

  const enrolledStudents = useMemo(
    () =>
      getEnrolledStudentsForCourse(
        course,
        bundleData?.batches,
        enrollmentsCatalog,
        studentsCatalog
      ),
    [course, bundleData?.batches, enrollmentsCatalog, studentsCatalog]
  );

  const handleBatchCreated = async () => {
    refetchBatches();
    await invalidateFormationGraph(queryClient);
    setShowBatchForm(false);
    setShowEditForm(false);
    setEditingBatch(null);
  };

  const handleDeleteBatch = async (batchId) => {
    if (!(await confirmDialog(t('coursesPage.confirmDeleteBatch')))) return;
    try {
      await formationAPI.deleteBatch(batchId);
      refetchBatches();
      await invalidateFormationGraph(queryClient);
    } catch (e) {
      console.error(e);
      notify.show(t('coursesPage.deleteError'));
    }
  };

  const handleViewStudents = (batch) => {
    const enrollments = enrollmentsCatalog.filter((e) => e.batchId === batch.id && e.status === 'active');
    const byId = Object.fromEntries(studentsCatalog.filter((s) => s?.id).map((s) => [s.id, s]));
    const students = enrollments
      .map((enrollment) => {
        const student = byId[enrollment.studentId];
        return student
          ? { ...student, enrollmentDate: enrollment.enrollmentDate, enrollmentId: enrollment.id }
          : null;
      })
      .filter(Boolean);

    setSelectedBatchStudents({ batch, students, enrollmentCount: enrollments.length });
    setShowStudentsModal(true);
  };

  const enrollmentCountByBatch = useMemo(
    () => countActiveEnrollmentsByBatch(enrollmentsCatalog),
    [enrollmentsCatalog]
  );

  const handleEditBatch = (batch) => {
    setEditingBatch(batch);
    setShowEditForm(true);
    setShowBatchForm(false);
  };

  const handleBatchUpdated = () => {
    handleBatchCreated();
  };

  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-1">{course.title}</h2>
            <p className="text-slate-600 dark:text-slate-400 text-sm">Code: {course.courseCode}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1.5 rounded-full text-sm ${
              course.status === 'active' 
              ? 'bg-green-500/20 text-green-400' 
              : course.status === 'pending' 
              ? 'bg-orange-500/20 text-orange-400' 
              : 'bg-gray-500/20 text-gray-400'
            }`}>
              {course.status === 'active' ? 'En cours' : course.status === 'pending' ? 'À venir' : 'Terminé'}
            </span>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-1 mb-6">
          <button
            onClick={() => setActiveTab('details')}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
              activeTab === 'details'
                ? 'bg-forma-600 text-white shadow-lg'
                : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80 hover:text-slate-900 dark:text-slate-50'
            }`}
          >
            <ClipboardDocumentCheckIcon className="h-4 w-4" />
            <span>{t('coursesPage.tabDetailsCourse')}</span>
          </button>
          <button
            onClick={() => setActiveTab('batches')}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
              activeTab === 'batches'
                ? 'bg-forma-600 text-white shadow-lg'
                : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80 hover:text-slate-900 dark:text-slate-50'
            }`}
          >
            <TagIcon className="h-4 w-4" />
            <span>{t('coursesPage.batchesTabHeading')} ({batches.length})</span>
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'details' && (
        <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <CalendarDaysIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-2" />
                <span className="text-slate-700 dark:text-slate-300 text-sm">{t('coursesPage.lblPeriodShort')}</span>
              </div>
              <p className="text-slate-900 dark:text-slate-50">
                {course.startDate && course.endDate
                  ? `Du ${new Date(course.startDate).toLocaleDateString()} au ${new Date(course.endDate).toLocaleDateString()}`
                  : course.startDate
                    ? `À partir du ${new Date(course.startDate).toLocaleDateString()}`
                    : course.endDate
                      ? `Jusqu'au ${new Date(course.endDate).toLocaleDateString()}`
                      : t('coursesPage.periodNotSet')}
              </p>
            </div>
            <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <ClockIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-2" />
                <span className="text-slate-700 dark:text-slate-300 text-sm">{t('coursesPage.lblDurationShort')}</span>
              </div>
              <p className="text-slate-900 dark:text-slate-50">{course.duration} {t('coursesPage.courseFormLblDurationHrs')?.replace(/\d+\s*/, '') || 'h'}</p>
            </div>
            <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <UserGroupIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-2" />
                <span className="text-slate-700 dark:text-slate-300 text-sm">{t('coursesPage.lblCapacityShort')}</span>
              </div>
              <p className="text-slate-900 dark:text-slate-50">
                {enrolledStudents.length} / {course.capacity} {t('coursesPage.capacitySuffix', { n: course.capacity }).replace(/\d+\s*/, '')}
              </p>
            </div>
            <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <CurrencyDollarIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-2" />
                <span className="text-slate-700 dark:text-slate-300 text-sm">{t('coursesPage.lblPriceShort')}</span>
              </div>
              <p className="text-slate-900 dark:text-slate-50">{course.price.toLocaleString()} DA</p>
            </div>
          </div>

          <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4">{t('coursesPage.sectionDescription')}</h3>
            <p className="text-slate-700 dark:text-slate-300">{course.description}</p>
          </div>
          
          <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4">{t('coursesPage.sectionTrainer')}</h3>
            <div className="flex items-center">
              <div className="h-12 w-12 rounded-full bg-forma-500/15 flex items-center justify-center mr-3">
                {course.trainer?.photo ? (
                  <img src={course.trainer.photo} alt={course.trainer.name || 'Formateur'} className="h-12 w-12 rounded-full object-cover" />
                ) : (
                  <UserGroupIcon className="h-6 w-6 text-forma-600 dark:text-forma-400" />
                )}
              </div>
              <div>
                <div className="text-slate-900 dark:text-slate-50 font-medium">{course.trainer?.name || t('coursesPage.trainerNotAssigned')}</div>
                <div className="text-slate-600 dark:text-slate-400 text-sm">{course.trainer?.speciality || t('coursesPage.trainerSpecialityMissing')}</div>
              </div>
            </div>
          </div>
          
          <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">
                {t('coursesPage.sectionEnrolledStudents')} ({enrolledStudents.length})
              </h3>
            </div>
            {enrolledStudents.length > 0 ? (
              <div className="space-y-3">
                {enrolledStudents.map((student) => (
                  <div key={student.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-100/80 dark:bg-slate-800/40">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-forma-500/15 flex items-center justify-center mr-3">
                        {student.photo ? (
                          <img src={student.photo} alt={student.name} className="h-8 w-8 rounded-full object-cover" />
                        ) : (
                          <AcademicCapIcon className="h-4 w-4 text-forma-600 dark:text-forma-400" />
                        )}
                      </div>
                      <div>
                        <div className="text-slate-900 dark:text-slate-50 text-sm font-medium">{student.name}</div>
                        <div className="text-slate-600 dark:text-slate-400 text-xs">
                          {student.email || student.phone || student.studentCode || ''}
                        </div>
                      </div>
                    </div>
                    <Link to={`/students/${student.id}`} className="text-sm font-semibold text-forma-700 hover:text-forma-600 dark:text-forma-400 dark:hover:text-forma-300">
                      {t('commonActions.viewDetails')}
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-600 dark:text-slate-400 italic">{t('coursesPage.noStudentYet')}</p>
            )}
          </div>
        </div>
      )}

      {/* Batches Tab */}
      {activeTab === 'batches' && (
        <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">{t('coursesPage.batchesTabHeading')}</h3>
            <button
              onClick={() => setShowBatchForm(!showBatchForm)}
              className={clsx(FORMA_LINK_BUTTON_CLASS, 'flex items-center space-x-2')}
            >
              <PlusIcon className="h-4 w-4" />
              <span>{t('coursesPage.batchCreateFirst') || 'Nouveau Groupe'}</span>
            </button>
          </div>

          {showBatchForm && (
            <div className="mb-6">
              <BatchForm 
                courseId={course.id}
                courseName={course.title}
                allBatchesForConflict={allBatchesForConflict}
                onBatchCreated={handleBatchCreated}
                onCancel={() => setShowBatchForm(false)}
              />
            </div>
          )}

          {showEditForm && editingBatch && (
            <div className="mb-6">
              <BatchForm 
                courseId={course.id}
                courseName={course.title}
                isEditing={true}
                batchData={editingBatch}
                allBatchesForConflict={allBatchesForConflict}
                onBatchCreated={handleBatchUpdated}
                onCancel={() => {
                  setShowEditForm(false);
                  setEditingBatch(null);
                }}
              />
            </div>
          )}

          {batches.length === 0 ? (
            <div className="text-center py-12">
              <TagIcon className="h-16 w-16 text-slate-900 dark:text-slate-50/30 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-2">{t('coursesPage.batchEmptyTitle')}</h3>
              <p className="text-slate-600 dark:text-slate-400 mb-6">{t('coursesPage.batchEmptySubtitle')}</p>
              {!showBatchForm && (
                <button
                  onClick={() => setShowBatchForm(true)}
                  className={clsx(FORMA_LINK_BUTTON_CLASS, 'mx-auto flex items-center space-x-2')}
                >
                  <PlusIcon className="h-4 w-4" />
                  <span>{t('coursesPage.batchCreateFirst')}</span>
                </button>
              )}
            </div>
          ) : (
            <div className="grid gap-4">
              {batches.map(batch => {
                const batchWithEnrollment = enrichBatchWithEnrollment(batch, enrollmentCountByBatch);
                return (
                  <div key={batch.id} className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="text-slate-900 dark:text-slate-50 font-medium">{batch.batchName}</h4>
                        <p className="text-slate-600 dark:text-slate-400 text-sm">
                          {batch.days.join(', ')} • {batch.startTime} - {batch.endTime}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          batchWithEnrollment?.isFull 
                            ? 'bg-red-500/20 text-red-400' 
                            : 'bg-green-500/20 text-green-400'
                        }`}>
                          {batchWithEnrollment?.currentEnrollment || 0}/{batch.maxCapacity}
                        </span>
                        <button
                          onClick={() => handleViewStudents(batch)}
                          className="p-1 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30 transition-colors"
                          title={t('commonActions.viewStudents')}
                        >
                          <UserGroupIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditBatch(batch)}
                          className="p-1 bg-yellow-500/20 text-yellow-400 rounded hover:bg-yellow-500/30 transition-colors"
                          title={t('commonActions.editGroup')}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteBatch(batch.id)}
                          className="p-1 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 transition-colors"
                          title={t('commonActions.delete')}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-slate-600 dark:text-slate-400">Formateur:</span>
                        <p className="text-slate-900 dark:text-slate-50">{batch.trainerName}</p>
                      </div>
                      <div>
                        <span className="text-slate-600 dark:text-slate-400">{t('coursesPage.lblRoomColon')}</span>
                        <p className="text-slate-900 dark:text-slate-50">{resolveRoomLabel(classrooms, batch.room, t('coursesPage.roomUndefined'))}</p>
                      </div>
                      <div>
                        <span className="text-slate-600 dark:text-slate-400">Début:</span>
                        <p className="text-slate-900 dark:text-slate-50">
                          {batch.startDate
                            ? new Date(batch.startDate).toLocaleDateString()
                            : t('coursesPage.roomUndefined')}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-600 dark:text-slate-400">Fin:</span>
                        <p className="text-slate-900 dark:text-slate-50">
                          {batch.endDate
                            ? new Date(batch.endDate).toLocaleDateString()
                            : t('coursesPage.roomUndefined')}
                        </p>
                      </div>
                    </div>

                    {/* Enrollment progress bar */}
                    <div className="mt-3">
                      <div className="flex justify-between text-xs text-slate-600 dark:text-slate-400 mb-1">
                        <span>{t('groupsPage.enrollmentShort')}</span>
                        <span>{((batchWithEnrollment?.currentEnrollment || 0) / batch.maxCapacity * 100).toFixed(0)}%</span>
                      </div>
                      <div className="w-full h-2 bg-slate-50 dark:bg-slate-800/80 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-forma-500 to-forma-600 rounded-full transition-all duration-300" 
                          style={{ width: `${((batchWithEnrollment?.currentEnrollment || 0) / batch.maxCapacity) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Students Modal */}
      {showStudentsModal && selectedBatchStudents && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="max-h-[80vh] max-w-4xl w-full overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 shadow-soft dark:border-slate-700 dark:bg-slate-900">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50">
                  Étudiants inscrits - {selectedBatchStudents.batch.batchName}
                </h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm">
                  {selectedBatchStudents.batch.days.join(', ')} • {selectedBatchStudents.batch.startTime} - {selectedBatchStudents.batch.endTime}
                </p>
              </div>
              <button
                onClick={() => setShowStudentsModal(false)}
                className="p-2 bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between">
                <span className="text-slate-700 dark:text-slate-300">
                  Total: {selectedBatchStudents.students.length} étudiant(s)
                </span>
                <span className="text-slate-600 dark:text-slate-400 text-sm">
                  Capacité: {selectedBatchStudents.students.length}/{selectedBatchStudents.batch.maxCapacity}
                </span>
              </div>
              <div className="w-full h-2 bg-slate-50 dark:bg-slate-800/80 rounded-full mt-2 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-forma-500 to-forma-600 rounded-full transition-all duration-300" 
                  style={{ width: `${(selectedBatchStudents.students.length / selectedBatchStudents.batch.maxCapacity) * 100}%` }}
                ></div>
              </div>
            </div>

            <div className="overflow-y-auto max-h-96">
              {selectedBatchStudents.students.length === 0 ? (
                <div className="text-center py-12">
                  <UserGroupIcon className="h-16 w-16 text-slate-900 dark:text-slate-50/30 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-2">{t('coursesPage.noStudentEnrolledTitle')}</h3>
                  <p className="text-slate-600 dark:text-slate-400">{t('coursesPage.noStudentEnrolledBody')}</p>
                </div>
              ) : (
                <div className="grid gap-3">
                  {selectedBatchStudents.students.map((student) => (
                    <div key={student.id} className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4 border border-slate-200 dark:border-slate-600">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 rounded-full bg-forma-500/15 flex items-center justify-center">
                            <UserGroupIcon className="h-5 w-5 text-forma-600 dark:text-forma-400" />
                          </div>
                          <div>
                            <h4 className="text-slate-900 dark:text-slate-50 font-medium">{student.name}</h4>
                            <p className="text-slate-600 dark:text-slate-400 text-sm">{student.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-slate-600 dark:text-slate-400 text-sm">
                            Inscrit le: {new Date(student.enrollmentDate).toLocaleDateString('fr-FR')}
                          </div>
                          <div className="text-slate-600 dark:text-slate-400 text-xs">
                            ID: {student.studentCode || student.id}
                          </div>
                        </div>
                      </div>
                      
                      {student.phone && (
                        <div className="mt-2 text-slate-600 dark:text-slate-400 text-sm">
                          📞 {student.phone}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6 pt-4 border-t border-slate-200 dark:border-slate-600">
              <button
                onClick={() => setShowStudentsModal(false)}
                className={clsx(FORMA_LINK_BUTTON_CLASS, 'px-6 py-2')}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

const CourseForm = ({ isEditing = false, courseData = null }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [trainers, setTrainers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: courseData?.title || "",
    courseCode: courseData?.courseCode || `COURSE-${new Date().getFullYear()}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
    startDate: courseData?.startDate ? new Date(courseData.startDate).toISOString().split('T')[0] : "",
    endDate: courseData?.endDate ? new Date(courseData.endDate).toISOString().split('T')[0] : "",
    duration: courseData?.duration || "",
    price: courseData?.price || "",
    capacity: courseData?.capacity || "",
    trainer: courseData?.trainer?.id || "",
    trainerObject: courseData?.trainer || null,
    status: courseData?.status || "pending",
    description: courseData?.description || "",
    enrolledStudents: courseData?.enrolledStudents || []
  });

  // Load trainers from D1
  useEffect(() => {
    let cancelled = false;
    spaEntityAPI.list('trainers').then((list) => {
      if (!cancelled) setTrainers(list || []);
    });
    return () => { cancelled = true; };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for trainer selection
    if (name === "trainer") {
      const selectedTrainer = trainers.find(trainer => trainer.id === value);
      setFormData(prev => ({
        ...prev,
        trainer: value,
        trainerObject: selectedTrainer || null
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const courseToSave = {
        ...formData,
        startDate: formData.startDate || null,
        endDate: formData.endDate || null,
        trainer: {
          id: formData.trainer,
          name: formData.trainerObject?.name || trainers.find(t => t.id === formData.trainer)?.name || "Unknown",
          speciality: formData.trainerObject?.speciality || trainers.find(t => t.id === formData.trainer)?.specialty || "Unknown",
          photo: formData.trainerObject?.photo || null
        }
      };

      delete courseToSave.trainerObject;

      if (isEditing && courseData?.id) {
        await spaEntityAPI.update('courses', courseData.id, { ...courseData, ...courseToSave });
      } else {
        await spaEntityAPI.create('courses', courseToSave);
      }

      await queryClient.invalidateQueries(['spa', 'courses']);
      navigate('/courses');
    } catch (error) {
      console.error('Error saving course data:', error);
      notify.show(t('trainerForm.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
      <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-6">
        {isEditing ? t('coursesPage.courseFormEditTitle') : t('coursesPage.courseFormAddTitle')}
      </h2>
      
      <form className="space-y-6" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Titre du cours
            </label>
            <input 
              type="text"
              name="title" 
              value={formData.title}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('coursesPage.courseFormLblStartDate')} ({t('coursesPage.optionalSuffix')})
            </label>
            <input 
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
            />
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('coursesPage.courseFormLblEndDate')} ({t('coursesPage.optionalSuffix')})
            </label>
            <input 
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
            />
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Durée (heures)
            </label>
            <input 
              type="number"
              name="duration"
              value={formData.duration}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Prix (DA)
            </label>
            <input 
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Capacité
            </label>
            <input 
              type="number"
              name="capacity"
              value={formData.capacity}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('coursesPage.courseFormLblTrainer')}
            </label>
            <select
              name="trainer"
              value={formData.trainer}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              required
            >
              <option value="">{t('coursesPage.batchSelectTrainer')}</option>
              {trainers.map(trainer => (
                <option key={trainer.id} value={trainer.id}>
                  {trainer?.name || `${t('coursesPage.trainerFallbackNamed', { id: trainer.id })}`}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Statut
            </label>
            <select 
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              required
            >
              <option value="pending">{t('coursesPage.courseFormOptPending')}</option>
              <option value="active">{t('coursesPage.courseFormOptActive')}</option>
              <option value="completed">{t('coursesPage.courseFormOptCompleted')}</option>
            </select>
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('coursesPage.courseFormLblDescription')}
            </label>
            <textarea 
              rows="4"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              required
            ></textarea>
          </div>
        </div>
        
        <div className="flex justify-end space-x-4">
          <Link 
            to="/courses"
            className="px-4 py-2 bg-slate-100/80 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-colors rounded-lg text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50"
          >
            {t('trainerForm.cancel')}
          </Link>
          <button
            type="submit"
            className={clsx(FORMA_LINK_BUTTON_CLASS, 'px-4 py-2')}
          >
            {isEditing ? t('coursesPage.courseFormSubmitUpdate') : t('coursesPage.courseFormSubmitAdd')}
          </button>
        </div>
      </form>
    </div>
  );
};

const CoursesPage = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [coursesPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState('all');

  // Multi-select state for bulk delete
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  const { data: courses = [], refetch: refetchCourses } = useQuery(
    SPA_COURSES_KEY,
    () => spaEntityAPI.list('courses'),
    { staleTime: 10_000 }
  );

  const { data: enrollmentBundle } = useQuery(
    FORMATION_ENROLLMENT_BUNDLE_KEY,
    fetchFormationEnrollmentBundle,
    { staleTime: 10_000 }
  );

  useEffect(() => {
    refetchCourses();
    queryClient.invalidateQueries(FORMATION_ENROLLMENT_BUNDLE_KEY);
  }, [location.pathname, refetchCourses, queryClient]);

  const totalEnrolled = useMemo(() => {
    return (enrollmentBundle?.enrollments || []).filter((e) => e.status === 'active').length;
  }, [enrollmentBundle?.enrollments]);

  const enrollmentByCourseId = useMemo(() => {
    const map = {};
    for (const course of courses) {
      map[course.id] = countEnrolledStudentsForCourse(
        course,
        enrollmentBundle?.batches,
        enrollmentBundle?.enrollments
      );
    }
    return map;
  }, [courses, enrollmentBundle?.batches, enrollmentBundle?.enrollments]);

  // Statistiques des cours (i18n)
  const stats = useMemo(
    () => [
      {
        title: t('coursesPage.statsTotalTitle'),
        value: courses.length.toString(),
        hint: t('coursesPage.statsTotalHint'),
        icon: ClipboardDocumentCheckIcon,
        iconWrap:
          'bg-forma-100 text-forma-700 shadow-inner dark:bg-forma-950/50 dark:text-forma-400',
      },
      {
        title: t('coursesPage.statsOpenTitle'),
        value: courses.filter((c) => c.status === 'active').length.toString(),
        hint: t('coursesPage.statsOpenHint'),
        icon: CalendarDaysIcon,
        iconWrap:
          'bg-emerald-100 text-emerald-800 shadow-inner dark:bg-emerald-950/45 dark:text-emerald-400',
      },
      {
        title: t('coursesPage.statsFilledTitle'),
        value: `${totalEnrolled}`,
        hint: t('coursesPage.statsFilledHint'),
        icon: AcademicCapIcon,
        iconWrap: 'bg-amber-100 text-amber-800 shadow-inner dark:bg-amber-950/40 dark:text-amber-400',
      },
    ],
    [courses, totalEnrolled, t]
  );

  const tabs = useMemo(
    () => [
      { id: 'all', name: t('coursesPage.tabAll') },
      { id: 'active', name: t('coursesPage.tabActive') },
      { id: 'pending', name: t('coursesPage.tabPending') },
      { id: 'completed', name: t('coursesPage.tabCompleted') },
    ],
    [t]
  );

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.courseCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (course.trainer && course.trainer.name && course.trainer.name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    if (!matchesSearch) return false;
    
    if (activeTab === 'all') return true;
    if (activeTab === 'active') return course.status === 'active';
    if (activeTab === 'pending') return course.status === 'pending';
    if (activeTab === 'completed') return course.status === 'completed';
    return true;
  });

  const tabCounts = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const matchesSearchRow = (course) => {
      if (!q) return true;
      return (
        (course.title && course.title.toLowerCase().includes(q)) ||
        (course.courseCode && course.courseCode.toLowerCase().includes(q)) ||
        (course.trainer && course.trainer.name && course.trainer.name.toLowerCase().includes(q))
      );
    };
    const base = courses.filter(matchesSearchRow);
    return {
      all: base.length,
      active: base.filter((c) => c.status === 'active').length,
      pending: base.filter((c) => c.status === 'pending').length,
      completed: base.filter((c) => c.status === 'completed').length,
    };
  }, [courses, searchTerm]);

  const paginatedCourses = filteredCourses.slice((currentPage - 1) * coursesPerPage, currentPage * coursesPerPage);
  const totalCourses = filteredCourses.length;
  const totalPages = Math.ceil(totalCourses / coursesPerPage);
  const pageSliceLabel =
    totalCourses === 0
      ? '—'
      : `${Math.min((currentPage - 1) * coursesPerPage + 1, totalCourses)}–${Math.min(currentPage * coursesPerPage, totalCourses)}`;

  const pageNav = useMemo(
    () => paginationItems(Math.min(Math.max(currentPage, 1), Math.max(totalPages, 1)), Math.max(totalPages, 1)),
    [currentPage, totalPages]
  );

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleDelete = async (courseId) => {
    if (await confirmDialog(t('coursesPage.confirmDeleteCourse'))) {
      try {
        await spaEntityAPI.delete('courses', courseId);
        await queryClient.invalidateQueries(SPA_COURSES_KEY);
        await invalidateFormationGraph(queryClient);
        setSelectedCourses((prev) => prev.filter((id) => id !== courseId));
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        notify.show(t('coursesPage.deleteError'));
      }
    }
  };

  // Multi-select functions
  const handleSelectCourse = (courseId) => {
    setSelectedCourses(prev => {
      if (prev.includes(courseId)) {
        // Remove from selection
        return prev.filter(id => id !== courseId);
      } else {
        // Add to selection
        return [...prev, courseId];
      }
    });
  };

  const handleSelectAll = () => {
    if (isSelectAll) {
      // Deselect all
      setSelectedCourses([]);
      setIsSelectAll(false);
    } else {
      // Select all visible courses
      const visibleCourseIds = paginatedCourses.map(c => c.id);
      setSelectedCourses(visibleCourseIds);
      setIsSelectAll(true);
    }
  };

  const handleBulkDelete = async () => {
    try {
      const ids = [...selectedCourses];
      await Promise.all(ids.map((courseId) => spaEntityAPI.delete('courses', courseId)));
      await queryClient.invalidateQueries(SPA_COURSES_KEY);

      setSelectedCourses([]);
      setIsSelectAll(false);
      setShowBulkDeleteModal(false);
    } catch (error) {
      console.error('Error deleting courses:', error);
      notify.show(t('coursesPage.bulkDeleteError'));
    }
  };

  // Auto-update select all state based on selected courses
  useEffect(() => {
    if (selectedCourses.length === 0) {
      setIsSelectAll(false);
    } else {
      const visibleCourseIds = paginatedCourses.map(c => c.id);
      const allVisibleSelected = visibleCourseIds.every(id => selectedCourses.includes(id));
      setIsSelectAll(allVisibleSelected && visibleCourseIds.length > 0);
    }
  }, [selectedCourses, paginatedCourses]);

  const formaLinkBtn = FORMA_LINK_BUTTON_CLASS;

  return (
    <>
    <Routes>
      <Route index element={
          <div className="space-y-6">
            <PageHeader
              title={t('coursesPage.title')}
              description={t('coursesPage.description')}
              actions={
                <Link to="/courses/add" className={formaLinkBtn}>
                  <PlusIcon className="h-5 w-5" />
                  <span>{t('coursesPage.newCourse')}</span>
                </Link>
              }
            />

            {/* Single workbook surface: KPIs + toolbar + list */}
            <UiCard padding={false} className="overflow-hidden">
              <div className="border-b border-slate-200/90 bg-gradient-to-br from-slate-50 via-white to-slate-50/80 dark:border-slate-700 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
                <div className="grid gap-6 p-6 sm:grid-cols-3 sm:gap-5">
                  {stats.map((stat, index) => (
                    <motion.div
                      key={stat.title}
                      layout
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ type: 'spring', stiffness: 380, damping: 28, delay: index * 0.06 }}
                      whileHover={{ y: -3 }}
                      className="group relative rounded-2xl border border-slate-200/80 bg-white/80 p-[1px] shadow-sm backdrop-blur-sm dark:border-slate-700/80 dark:bg-slate-950/55"
                    >
                      <div className="relative overflow-hidden rounded-[0.9375rem] bg-gradient-to-b from-white/90 to-white/35 p-4 dark:from-slate-950/95 dark:to-slate-950/65">
                        <div
                          aria-hidden
                          className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br from-forma-200/35 to-transparent opacity-70 blur-2xl transition group-hover:opacity-100 dark:from-forma-600/25"
                        />
                        <div className="relative flex gap-4">
                          <div
                            className={clsx(
                              'relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ring-2 ring-white/70 dark:ring-slate-800/80',
                              stat.iconWrap
                            )}
                          >
                            <stat.icon className="h-6 w-6" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                              {stat.title}
                            </p>
                            <p className="mt-0.5 text-3xl font-bold tabular-nums tracking-tight text-slate-900 dark:text-white">
                              {stat.value}
                            </p>
                            <p className="mt-1 border-t border-slate-100 pt-2 text-xs text-slate-500 dark:border-slate-700/70 dark:text-slate-400">
                              {stat.hint}
                            </p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Filters */}
              <div className="border-b border-slate-200 bg-white/40 px-5 py-4 dark:border-slate-700 dark:bg-slate-900/35">
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
                  <FilterSelect
                    id="course-status-filter"
                    label={t('coursesPage.statusLabel')}
                    value={activeTab}
                    onChange={(v) => {
                      setActiveTab(v);
                      setCurrentPage(1);
                    }}
                    options={tabs.map((tab) => ({
                      id: tab.id,
                      name: tab.name,
                      count: tabCounts[tab.id],
                    }))}
                    className="sm:min-w-[11rem]"
                  />
                  <div className="relative min-w-0 flex-1 sm:min-w-[14rem]">
                    <label htmlFor="course-search" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      {t('coursesPage.searchSrOnly')}
                    </label>
                    <MagnifyingGlassIcon className="pointer-events-none absolute bottom-2.5 h-5 w-5 -translate-y-1/2 text-slate-400 ltr:left-3 rtl:right-3" />
                    <input
                      id="course-search"
                      type="search"
                      autoComplete="off"
                      placeholder={t('coursesPage.searchPlaceholder')}
                      value={searchTerm}
                      onChange={handleSearch}
                      className={clsx(
                        'w-full rounded-xl border border-slate-200 bg-white py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-forma-500 focus:outline-none focus:ring-2 focus:ring-forma-500/25 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500 ltr:pl-10 rtl:pr-10',
                        searchTerm.trim() ? 'ltr:pr-10 rtl:pl-10' : 'ltr:pr-4 rtl:pl-4'
                      )}
                    />
                    {searchTerm.trim() !== '' ? (
                      <button
                        type="button"
                        aria-label={t('coursesPage.clearSearchAria')}
                        onClick={() => {
                          setSearchTerm('');
                          setCurrentPage(1);
                        }}
                        className="absolute bottom-1.5 rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 ltr:right-2 rtl:left-2 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>

              {(searchTerm.trim() !== '' || activeTab !== 'all') && (
                <div className="flex flex-wrap items-center gap-2 border-b border-forma-200/60 bg-forma-50/50 px-5 py-2.5 dark:border-forma-900/35 dark:bg-forma-950/25">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-forma-800 dark:text-forma-400">
                    {t('coursesPage.filtersActive')}
                  </span>
                  {searchTerm.trim() !== '' ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-forma-200/70 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm dark:border-forma-900/60 dark:bg-slate-950 dark:text-slate-200">
                      « {searchTerm.trim()} »
                    </span>
                  ) : null}
                  {activeTab !== 'all' ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-forma-200/70 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm dark:border-forma-900/60 dark:bg-slate-950 dark:text-slate-200">
                      {tabs.find((tab) => tab.id === activeTab)?.name}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm('');
                      setActiveTab('all');
                      setCurrentPage(1);
                    }}
                    className="ml-auto rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    {t('coursesPage.resetFilters')}
                  </button>
                </div>
              )}
              {selectedCourses.length > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-forma-200/80 bg-forma-50/90 px-5 py-3 dark:border-forma-900/50 dark:bg-forma-950/25">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
                      {selectedCourses.length === 1
                        ? t('coursesPage.selectedSingle')
                        : t('coursesPage.selectedPlural', { n: selectedCourses.length })}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCourses([]);
                        setIsSelectAll(false);
                      }}
                      className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                    >
                      {t('coursesPage.deselectAll')}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowBulkDeleteModal(true)}
                    className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700"
                  >
                    <TrashIcon className="h-4 w-4" />
                    {t('coursesPage.deleteBulk', { n: selectedCourses.length })}
                  </button>
                </div>
              )}

              {paginatedCourses.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-5 px-6 py-16 text-center">
                  <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/60">
                    <ClipboardDocumentCheckIcon className="h-10 w-10 text-forma-500/70 dark:text-forma-400/80" />
                  </div>
                  <div className="max-w-md space-y-2">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                      {t('coursesPage.emptyTitle')}
                    </h3>
                    <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                      {t('coursesPage.emptyBody')}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setSearchTerm('');
                        setActiveTab('all');
                        setCurrentPage(1);
                      }}
                      className="mx-auto rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      {t('coursesPage.clearFiltersBtn')}
                    </button>
                  </div>
                  <Link to="/courses/add" className={formaLinkBtn}>
                    <PlusIcon className="h-5 w-5" />
                    <span>{t('coursesPage.addCourseShortcut')}</span>
                  </Link>
                </div>
              ) : (
                <>
                  <div className="border-b border-slate-200 bg-gradient-to-r from-white to-slate-50/95 px-6 py-4 dark:border-slate-700 dark:from-slate-900 dark:to-slate-950/90">
                    <h2 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white">
                      {t('coursesPage.listHeading')}
                    </h2>
                    <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-600 dark:text-slate-400">
                      <span>
                        <span className="font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                          {totalCourses}
                        </span>{' '}
                        {t(
                          totalCourses === 1
                            ? 'coursesPage.resultsSingular'
                            : 'coursesPage.resultsPlural'
                        )}
                      </span>
                      <span className="text-slate-300 dark:text-slate-600" aria-hidden>
                        ·
                      </span>
                      <span>
                        {t('coursesPage.rangeOn')}{' '}
                        <span className="font-medium tabular-nums text-slate-800 dark:text-slate-200">
                          {pageSliceLabel}
                        </span>{' '}
                        {t('coursesPage.onTotal')}{' '}
                        <span className="font-medium tabular-nums text-slate-800 dark:text-slate-200">
                          {totalCourses}
                        </span>
                      </span>
                      <span className="text-slate-300 dark:text-slate-600" aria-hidden>
                        ·
                      </span>
                      <span className="text-slate-500 dark:text-slate-500">
                        {coursesPerPage} {t('coursesPage.perPage')}
                      </span>
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[720px] border-collapse text-left">
                      <thead className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 text-[11px] font-bold uppercase tracking-wider text-slate-500 shadow-[0_1px_0_rgba(15,23,42,0.06)] backdrop-blur-md dark:border-slate-700 dark:bg-slate-900/95 dark:text-slate-400">
                        <tr>
                          <th scope="col" className="w-12 px-4 py-3.5">
                            <span className="sr-only">{t('coursesPage.srSelection')}</span>
                            <input
                              type="checkbox"
                              checked={isSelectAll}
                              onChange={handleSelectAll}
                              aria-label={t('coursesPage.selectAllPageAria')}
                              className="h-4 w-4 rounded border-slate-300 text-forma-600 focus:ring-forma-500 dark:border-slate-600 dark:bg-slate-900"
                            />
                          </th>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            {t('coursesPage.colCourse')}
                          </th>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            {t('coursesPage.colPeriod')}
                          </th>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            {t('coursesPage.colTrainerCol')}
                          </th>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            {t('coursesPage.colEnrollments')}
                          </th>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            {t('coursesPage.colStatusCol')}
                          </th>
                          <th scope="col" className="px-4 py-3.5 text-right font-bold">
                            {t('coursesPage.colActions')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedCourses.map((course, rowIdx) => (
                          <motion.tr
                            key={course.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: Math.min(rowIdx * 0.03, 0.35) }}
                            className={clsx(
                              'border-b border-slate-100 transition-colors dark:border-slate-800/80',
                              rowIdx % 2 === 1 && 'bg-slate-50/45 dark:bg-slate-900/40',
                              'hover:bg-forma-50/50 dark:hover:bg-forma-950/20',
                              selectedCourses.includes(course.id) &&
                                'bg-forma-100/55 ring-1 ring-inset ring-forma-200/80 dark:bg-forma-950/40 dark:ring-forma-800/60'
                            )}
                          >
                            <td className="whitespace-nowrap px-4 py-3.5">
                              <input
                                type="checkbox"
                                checked={selectedCourses.includes(course.id)}
                                onChange={() => handleSelectCourse(course.id)}
                                aria-label={t('coursesPage.selectCourseRowAria', { name: course.title })}
                                className="h-4 w-4 rounded border-slate-300 text-forma-600 focus:ring-forma-500 dark:border-slate-600 dark:bg-slate-900"
                              />
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5">
                              <div className="flex items-center">
                                <div className="mr-3 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-forma-100 dark:bg-forma-950/50">
                                  <ClipboardDocumentCheckIcon className="h-5 w-5 text-forma-600 dark:text-forma-400" />
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-slate-900 dark:text-slate-50">
                                    {course.title}
                                  </div>
                                  <div className="text-xs text-slate-600 dark:text-slate-400">
                                    {course.courseCode}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5">
                              <div className="text-sm text-slate-700 dark:text-slate-300">
                                {course.startDate
                                  ? new Date(course.startDate).toLocaleDateString()
                                  : t('coursesPage.roomUndefined')}
                              </div>
                              <div className="text-xs text-slate-600 dark:text-slate-400">
                                {course.endDate
                                  ? `${t('coursesPage.endsOnPrefix')} ${new Date(course.endDate).toLocaleDateString()}`
                                  : t('coursesPage.periodNotSet')}
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5">
                              <div className="text-sm text-slate-700 dark:text-slate-300">
                                {course.trainer?.name || t('coursesPage.trainerNotAssigned')}
                              </div>
                              <div className="text-xs text-slate-600 dark:text-slate-400">
                                {course.trainer?.speciality || t('coursesPage.trainerSpecialityMissing')}
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5">
                              <div className="text-sm text-slate-700 dark:text-slate-300">
                                {(enrollmentByCourseId[course.id] || 0)} / {course.capacity}
                              </div>
                              <div className="w-24 h-1.5 bg-slate-50 dark:bg-slate-800/80 rounded-full overflow-hidden mt-1">
                                <div 
                                  className="h-full rounded-full bg-gradient-to-r from-forma-500 to-forma-700" 
                                  style={{
                                    width: `${course.capacity ? Math.min(100, ((enrollmentByCourseId[course.id] || 0) / course.capacity) * 100) : 0}%`,
                                  }}
                                ></div>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5">
                              <span
                                className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                                  course.status === 'active'
                                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-400'
                                    : course.status === 'pending'
                                    ? 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-400'
                                    : 'bg-slate-100 text-slate-600 dark:bg-slate-600/25 dark:text-slate-400'
                                }`}
                              >
                                {course.status === 'active'
                                  ? t('coursesPage.statusLblActiveRow')
                                  : course.status === 'pending'
                                    ? t('coursesPage.statusLblPendingRow')
                                    : t('coursesPage.statusLblCompletedRow')}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5 text-right text-sm">
                              <div className="flex justify-end gap-1.5">
                                <Link
                                  to={`/courses/${course.id || course.courseCode || 'unknown'}`}
                                  className="rounded-lg border border-slate-200 bg-white p-2 text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-forma-600 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-forma-400"
                                  title={t('commonActions.viewDetails')}
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </Link>
                                <Link
                                  to={`/courses/edit/${course.id}`}
                                  className="rounded-lg border border-slate-200 bg-white p-2 text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-amber-600 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800"
                                  title={t('commonActions.edit')}
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </Link>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(course.id)}
                                  className="rounded-lg border border-slate-200 bg-white p-2 text-red-600 shadow-sm transition hover:bg-red-50 dark:border-slate-600 dark:bg-slate-900 dark:text-red-400 dark:hover:bg-red-950/30"
                                  title={t('commonActions.delete')}
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {totalCourses > coursesPerPage && (
                    <div className="flex flex-col gap-4 border-t border-slate-200 px-6 py-4 dark:border-slate-600 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-center text-sm text-slate-600 dark:text-slate-400 sm:text-left">
                        {t('coursesPage.paginationSummary', {
                          range: pageSliceLabel,
                          total: totalCourses,
                          page: currentPage,
                          pages: totalPages,
                        })}
                      </p>
                      <nav
                        className="flex flex-wrap items-center justify-center gap-1.5 sm:justify-end"
                        aria-label={t('coursesPage.paginationAria')}
                      >
                        <button
                          type="button"
                          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                          <ChevronLeftIcon className="h-4 w-4" aria-hidden />
                          {t('coursesPage.prevPage')}
                        </button>
                        <div className="mx-1 flex flex-wrap items-center justify-center gap-1">
                          {pageNav.map((item, idx) =>
                            item.type === 'ellipsis' ? (
                              <span
                                key={`ellipsis-${idx}`}
                                className="px-2 font-medium text-slate-400 dark:text-slate-500"
                                aria-hidden
                              >
                                …
                              </span>
                            ) : (
                              <button
                                key={item.page}
                                type="button"
                                onClick={() => setCurrentPage(item.page)}
                                className={clsx(
                                  'min-w-[2.5rem] rounded-lg px-2 py-1.5 text-sm font-semibold tabular-nums transition',
                                  item.page === currentPage
                                    ? 'bg-forma-600 text-white shadow-sm'
                                    : 'border border-transparent text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800'
                                )}
                              >
                                {item.page}
                              </button>
                            )
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                          {t('coursesPage.nextPage')}
                          <ChevronRightIcon className="h-4 w-4" aria-hidden />
                        </button>
                      </nav>
                    </div>
                  )}
                </>
              )}
            </UiCard>
          </div>
        }
      />
      <Route
        path="add"
        element={
          <FormPageLayout backTo="/courses" backLabel={t('coursesPage.detailBackToList')}>
            <CourseForm />
          </FormPageLayout>
        }
      />
      <Route
        path="edit/:id"
        element={
          <EditCourseRoute />
        }
      />
      <Route
        path=":id"
        element={
          <ViewCourseRoute />
        }
      />
    </Routes>

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-600 dark:bg-slate-900 w-full max-w-md"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <TrashIcon className="h-6 w-6 text-red-400" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50">
                {t('coursesPage.bulkModalTitle')}
              </h3>
            </div>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              {selectedCourses.length === 1
                ? t('coursesPage.bulkDeleteAskOne')
                : t('coursesPage.bulkDeleteAskMany', { n: selectedCourses.length })}
            </p>
            <div className="flex gap-3 w-full">
              <button
                onClick={() => setShowBulkDeleteModal(false)}
                className="flex-1 px-4 py-2 bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                {t('coursesPage.bulkDeleteCancel')}
              </button>
              <button
                onClick={handleBulkDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                {t('coursesPage.bulkDeleteConfirm')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

// Route components for edit and view course
const EditCourseRoute = () => {
  const { t } = useLanguage();
  const { id } = useParams();
  const { data: course, isLoading } = useQuery(
    ['spa', 'courses', id],
    () => spaEntityAPI.getById('courses', id),
    { enabled: Boolean(id) }
  );

  if (isLoading || !course) {
    return <div className="text-slate-900 dark:text-slate-50">Chargement...</div>;
  }
  
  return (
    <FormPageLayout backTo="/courses" backLabel={t('coursesPage.detailBackToList')}>
      <CourseForm isEditing={true} courseData={course} />
    </FormPageLayout>
  );
};

const ViewCourseRoute = () => {
  const { t } = useLanguage();
  const { id } = useParams();
  const { data: course, isLoading, isError } = useQuery(
    ['spa', 'courses', id],
    () => spaEntityAPI.getById('courses', id),
    { enabled: Boolean(id) }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-900 dark:text-slate-50">Chargement des détails du cours...</div>
      </div>
    );
  }

  if (isError || !course) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-700 dark:text-slate-300">
          <p>Cours introuvable (ID: {id})</p>
          <Link to="/courses" className="text-blue-400 hover:text-blue-300 mt-2 inline-block">
            ← Retour à la liste des cours
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <FormPageLayout backTo="/courses" backLabel={t('coursesPage.detailBackToList')}>
      <CourseDetail course={course} />
    </FormPageLayout>
  );
};

export default CoursesPage; 